import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown, PlayCircle } from "lucide-react";
import { api } from "@/lib/api";
import { Shell } from "@/components/Shell";
import { Empty, Loader, Pill } from "@/components/Bits";

const tone = { completed: "green", in_progress: "amber", not_started: "slate" };

export default function Library() {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/library").then(({ data }) => setData(data));
  }, []);

  if (!data) return <Shell><Loader label="Loading your library…" /></Shell>;
  if (!data.has_curriculum)
    return (
      <Shell>
        <Empty title="No approved curriculum yet" note="Your library fills up as soon as your guardian approves a curriculum." />
      </Shell>
    );

  return (
    <Shell>
      <div className="reveal" data-testid="library-root">
        <p className="rule-label">{data.curriculum.label} · nothing is locked</p>
        <h1 className="mt-3 text-3xl sm:text-4xl font-extrabold">Library</h1>
        <p className="mt-2 text-sm text-slate-500 max-w-2xl">
          Open any topic whenever you like. The planner adjusts your day around what you choose, and will nudge you if a
          chapter would land better first.
        </p>

        <div className="mt-8 grid sm:grid-cols-3 gap-4">
          {[
            ["Topics", `${data.topics.completed}/${data.topics.total}`],
            ["Chapters finished", `${data.chapters.completed}/${data.chapters.total}`],
            ["Subjects finished", `${data.subjects.completed}/${data.subjects.total}`],
          ].map(([k, v]) => (
            <div key={k} className="card-flat p-6">
              <p className="rule-label">{k}</p>
              <p className="mt-3 font-display text-3xl font-extrabold">{v}</p>
            </div>
          ))}
        </div>

        <div className="mt-8 space-y-5">
          {data.tree.map((subject) => (
            <div key={subject.subject_id} className="card-flat p-6" data-testid={`library-subject-${subject.subject_id}`}>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <h3 className="font-display text-xl font-semibold">{subject.name}</h3>
                <span className="text-sm text-slate-500">
                  {subject.progress.completed}/{subject.progress.total} topics · {subject.progress.percent}%
                </span>
              </div>
              <div className="mt-3 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                <div className="h-full bg-teal-600 transition-all duration-500" style={{ width: `${subject.progress.percent}%` }} />
              </div>

              <div className="mt-5 space-y-2">
                {subject.chapters.map((chapter) => {
                  const isOpen = open[chapter.chapter_id] ?? false;
                  return (
                    <div key={chapter.chapter_id} className="border border-slate-200 rounded-xl">
                      <button
                        data-testid={`library-chapter-${chapter.chapter_id}`}
                        onClick={() => setOpen({ ...open, [chapter.chapter_id]: !isOpen })}
                        className="w-full flex items-center gap-3 px-5 py-4 text-left"
                      >
                        <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${isOpen ? "" : "-rotate-90"}`} />
                        <span className="font-semibold text-sm">{chapter.name}</span>
                        <span className="ml-auto flex items-center gap-2 shrink-0">
                          <span className="text-xs text-slate-500">{chapter.est_hours} h</span>
                          <Pill tone={chapter.progress.completed === chapter.progress.total && chapter.progress.total ? "green" : "slate"}>
                            {chapter.progress.completed}/{chapter.progress.total}
                          </Pill>
                          <span
                            role="button"
                            tabIndex={0}
                            data-testid={`library-open-chapter-${chapter.chapter_id}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/chapter/${chapter.chapter_id}`);
                            }}
                            className="px-3 py-1.5 rounded-full border border-slate-300 text-xs font-semibold hover:border-slate-900 transition-colors"
                          >
                            Open chapter
                          </span>
                        </span>
                      </button>
                      {isOpen && (
                        <ul className="px-5 pb-4 divide-y divide-slate-100">
                          {chapter.topics.map((topic) => (
                            <li key={topic.topic_id} className="py-3 flex flex-wrap items-center gap-3 justify-between">
                              <div className="max-w-xl">
                                <p className="text-sm font-medium">{topic.title}</p>
                                {topic.best_score ? (
                                  <p className="mt-0.5 text-xs text-slate-500">best {topic.best_score}%</p>
                                ) : null}
                              </div>
                              <span className="flex items-center gap-3 shrink-0">
                                <Pill tone={tone[topic.status]}>{topic.status.replace("_", " ")}</Pill>
                                <button
                                  data-testid={`library-open-${topic.topic_id}`}
                                  onClick={() => navigate(`/topic/${topic.topic_id}`)}
                                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-slate-900 text-white text-xs font-semibold hover:bg-slate-800 transition-colors"
                                >
                                  <PlayCircle className="w-3.5 h-3.5" /> Open
                                </button>
                              </span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </Shell>
  );
}
